/**
 * SignalR connection manager.
 *
 * Establishes a persistent WebSocket connection to the backend hub and wires
 * incoming events to the Zustand user store.
 *
 * Events handled:
 *  - Dashboard  → full initial payload (user + balances + tickets + notifications)
 *  - MasterData → reference data (currencies, statuses, etc.)
 *  - Balance    → single balance update
 *  - User       → partial user update
 *  - Noti       → new notification(s)
 *
 * Ported from V1 with the same reconnection logic.
 */

import * as signalR from '@microsoft/signalr'
import type { AuthSession } from '@/auth/AuthContext'
import { useUserStore } from '@/store/user-store'
import { config } from '@/lib/config'
import { resolveClientFeature } from '@/lib/feature/resolveClientFeature'
import { FEATURE_QUERY_PARAM } from '@/lib/feature/feature.constants'

// ---------------------------------------------------------------------------
// Module-level state (singleton connection)
// ---------------------------------------------------------------------------

let connection:          signalR.HubConnection | null = null
let reconnectionAttempts = 0
const MAX_RECONNECTS     = 10
let isManualDisconnect   = false

// ---------------------------------------------------------------------------
// Logging de notificaciones — agrupado y en tabla para inspeccionar fácil lo
// que llega por SignalR (evento `Noti` y carga inicial `Dashboard`).
// ---------------------------------------------------------------------------

function logNotifications(source: string, rawPayload?: unknown): void {
  try {
    const all    = useUserStore.getState().user.notifications
    const unread = all.filter(n => n.pending).length

    const titleStyle = 'background:#6C63FF;color:#fff;padding:2px 7px;border-radius:4px;font-weight:bold'
    const metaStyle  = 'color:#9CA3AF;font-weight:normal'

    // eslint-disable-next-line no-console
    console.groupCollapsed(
      `%c🔔 SignalR · ${source}%c  ${all.length} total · ${unread} sin leer`,
      titleStyle, metaStyle,
    )
    // eslint-disable-next-line no-console
    console.table(
      all.map(n => ({
        id:      n.id,
        pending: n.pending,
        leída:   n.pending ? '—' : '✓',
        título:  n.title,
        mensaje: n.message,
        fecha:   n.date,
      })),
    )
    if (rawPayload !== undefined) {
      // eslint-disable-next-line no-console
      console.log('payload crudo:', rawPayload)
    }
    // eslint-disable-next-line no-console
    console.groupEnd()
  } catch (e) {
    console.error('[SignalR] noti log error:', e)
  }
}

// ---------------------------------------------------------------------------
// Raw Noti pub/sub — consumers can subscribe to raw SignalR Noti messages
// ---------------------------------------------------------------------------

const rawNotiSubscribers = new Set<(msg: unknown) => void>()

/** Subscribe to raw SignalR Noti messages. Returns an unsubscribe function. */
export function subscribeRawNoti(cb: (msg: unknown) => void): () => void {
  rawNotiSubscribers.add(cb)
  return () => rawNotiSubscribers.delete(cb)
}

const rawBalanceSubscribers = new Set<(msg: unknown) => void>()

/** Subscribe to raw SignalR Balance messages. Returns an unsubscribe function. */
export function subscribeRawBalance(cb: (msg: unknown) => void): () => void {
  rawBalanceSubscribers.add(cb)
  return () => rawBalanceSubscribers.delete(cb)
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function startConnection(session: AuthSession): Promise<void> {
  const {
    setUserData,
    setMasterData,
    setIsLoadedData,
    setIsConnecting,
    getUser,
    updateBalanceData,
    updateUserData,
    updateNotifications,
  } = useUserStore.getState()

  if (!config.signalrUrl) {
    console.warn('[SignalR] VITE_SIGNALR_URL is not configured — connection skipped.')
    return
  }

  const { id } = getUser()
  const token = session.bearerToken

  // Ensure any existing connection is fully stopped before creating a new one.
  // Without this await, a logout-triggered stopConnection() (fire-and-forget) may
  // still be mid-close when re-login calls startConnection(), causing the guard
  // to see state === Connected and return early — Dashboard never fires.
  if (connection) {
    await stopConnection()
  }

  try {
    setIsConnecting(true)
    isManualDisconnect = false

    const feature = resolveClientFeature()
    const hubUrl = feature
      ? `${config.signalrUrl}?${FEATURE_QUERY_PARAM}=${feature}`
      : config.signalrUrl

    connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation:    true,
        transport:          signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => token,
        timeout:            30_000,
      })
      .withAutomaticReconnect([0, 2_000, 5_000, 10_000, 15_000, 30_000])
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    // Dashboard — always register so re-login after any logout path works correctly.
    // Show loading skeleton only on fresh connect (store empty = id === 0).
    if (id === 0) setIsLoadedData(true)
    connection.on('Dashboard', message => {
      try {
        if (message == null) { return }
        setUserData(message)
        setIsLoadedData(false)
        reconnectionAttempts = 0
        logNotifications('Dashboard (carga inicial)')
      } catch (e) {
        console.error('[SignalR] Dashboard handler error:', e)
      }
    })

    connection.on('MasterData', message => {
      try { setMasterData(message) }
      catch (e) { console.error('[SignalR] MasterData error:', e) }
    })

    connection.on('Balance', message => {
      try {
        updateBalanceData(message)
        // Dispatch to raw subscribers (e.g. send-money conciliation)
        rawBalanceSubscribers.forEach(cb => {
          try { cb(message) } catch (e) { console.error('[SignalR] rawBalance subscriber error:', e) }
        })
        useUserStore.getState().clearPendingTransfers()
      }
      catch (e) { console.error('[SignalR] Balance error:', e) }
    })

    connection.on('User', message => {
      try { updateUserData(message) }
      catch (e) { console.error('[SignalR] User error:', e) }
    })

    connection.on('Noti', message => {
      try {
        updateNotifications(message)
        logNotifications('Noti (nueva)', message)
        // Broadcast to raw subscribers (e.g. send-money conciliation)
        rawNotiSubscribers.forEach(cb => {
          try { cb(message) } catch (e) { console.error('[SignalR] rawNoti subscriber error:', e) }
        })
        // Any Noti means the backend processed a transfer → clear the pending banner.
        useUserStore.getState().clearPendingTransfers()
      }
      catch (e) { console.error('[SignalR] Noti error:', e) }
    })

    // Connection lifecycle events
    connection.onclose(async error => {
      setIsConnecting(false)
      if (isManualDisconnect) { connection = null; return }

      if (reconnectionAttempts < MAX_RECONNECTS) {
        reconnectionAttempts++
        const delay = Math.min(1_000 * Math.pow(2, reconnectionAttempts), 30_000)
        setTimeout(async () => {
          connection = null
          try { await startConnection(session) }
          catch (e) { console.error('[SignalR] Reconnect error:', e) }
        }, delay)
      } else {
        console.error('[SignalR] Max reconnection attempts reached.', error)
        connection = null
        setIsLoadedData(false)
      }
    })

    connection.onreconnecting(() => {
      setIsConnecting(false)
      setIsLoadedData(true)
    })

    connection.onreconnected(() => {
      setIsConnecting(true)
      setIsLoadedData(false)
      reconnectionAttempts = 0
    })

    await connection.start()
    setIsConnecting(true)
    reconnectionAttempts = 0
  } catch (err) {
    setIsConnecting(false)
    console.error('[SignalR] Connection error:', err)

    if (reconnectionAttempts < MAX_RECONNECTS && !isManualDisconnect) {
      reconnectionAttempts++
      const delay = Math.min(3_000 * reconnectionAttempts, 30_000)
      setTimeout(() => {
        connection = null
        startConnection(session).catch(console.error)
      }, delay)
    } else {
      connection = null
    }
  }
}

export async function stopConnection(): Promise<void> {
  if (!connection) return
  try {
    isManualDisconnect = true
    await connection.stop()
  } catch (e) {
    console.error('[SignalR] Stop error:', e)
  } finally {
    connection          = null
    reconnectionAttempts = 0
  }
}
